import { useEffect, useState } from 'react';
import api from '../api/axios';
import { buildAssetUrl } from '../utils/api';
import {
  UserPlus, Mail, Shield, Camera, Edit3, Trash2,
  Briefcase, X, ShieldCheck, UserCog
} from 'lucide-react';

const FORM_INICIAL = {
  nombre: '',
  correo: '',
  password: '',
  rol: 'asesor_externo',
  foto_url: ''
};

export default function GestionUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [form, setForm] = useState(FORM_INICIAL);
  const [fotoArchivo, setFotoArchivo] = useState(null);
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsuarios(res.data);
    } catch (err) {
      setError('Error de privilegios o conexión al cargar equipo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const iniciarEdicion = (u) => {
    setForm({
      nombre: u.nombre,
      correo: u.correo,
      password: '',
      rol: u.rol,
      foto_url: u.foto_url || ''
    });
    setFotoArchivo(null);
    setEditando(u.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelarEdicion = () => {
    setForm(FORM_INICIAL);
    setFotoArchivo(null);
    setEditando(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let fotoUrl = form.foto_url;
      if (fotoArchivo) {
        const fd = new FormData();
        fd.append('foto', fotoArchivo);
        const upload = await api.post('/users/upload', fd);
        fotoUrl = upload.data.url;
      }

      const payload = { ...form, foto_url: fotoUrl };
      if (editando) {
        const res = await api.put(`/users/${editando}`, payload);
        setUsuarios(prev => prev.map(u => (u.id === editando ? res.data : u)));
      } else {
        const res = await api.post('/users', payload);
        setUsuarios(prev => [...prev, res.data]);
      }
      cancelarEdicion();
    } catch {
      setError('No se pudo procesar la solicitud del usuario.');
    }
  };

  const eliminarUsuario = async (id) => {
    if (!window.confirm('¿Deseas remover a este integrante del equipo?')) return;
    try {
      await api.delete(`/users/${id}`);
      setUsuarios(prev => prev.filter(u => u.id !== id));
    } catch { setError('Error al intentar eliminar.'); }
  };

  const getRolBadge = (rol) => {
    const styles = {
      superadmin: 'bg-slate-900 text-white',
      admin: 'bg-blue-100 text-blue-700',
      asesor_interno: 'bg-emerald-100 text-emerald-700',
      asesor_externo: 'bg-slate-100 text-slate-600'
    };
    return styles[rol] || styles.asesor_externo;
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pt-28 pb-20 px-6">
      <div className="max-w-7xl mx-auto">
        
        <header className="mb-12">
          <span className="text-blue-600 text-[10px] font-black uppercase tracking-[0.4em] mb-2 block italic">
            Human Resources Management
          </span>
          <h1 className="text-5xl font-serif text-slate-900 leading-tight">
            Gestión de <span className="italic">Equipo</span>
          </h1>
        </header>

        {error && (
          <div className="mb-8 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-xl text-sm flex items-center gap-3">
            <X size={18} /> {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* COLUMNA FORMULARIO */}
          <aside className="lg:col-span-4">
            <div className="sticky top-32 bg-white rounded-[2.5rem] p-8 shadow-2xl shadow-slate-200/50 border border-slate-100">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white shadow-lg">
                  {editando ? <UserCog size={22}/> : <UserPlus size={22}/>}
                </div>
                <div>
                  <h2 className="text-xl font-serif text-slate-900">{editando ? 'Editar Miembro' : 'Nuevo Registro'}</h2>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Configuración de acceso</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 ml-2">Información Personal</label>
                  <input className="user-input" placeholder="Nombre completo" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required />
                  <input type="email" className="user-input" placeholder="Email corporativo" value={form.correo} onChange={e => setForm({ ...form, correo: e.target.value })} required />
                  {!editando && <input type="password" className="user-input" placeholder="Password inicial" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />}
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 ml-2">Nivel de Privilegio</label>
                  <select className="user-input" value={form.rol} onChange={e => setForm({ ...form, rol: e.target.value })}>
                    <option value="asesor_externo">Asesor Externo</option>
                    <option value="asesor_interno">Asesor Interno</option>
                    <option value="admin">Administrador</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400 ml-2">Imagen de Perfil</label>
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-200 rounded-[1.5rem] cursor-pointer hover:bg-slate-50 transition-all overflow-hidden group">
                    <input type="file" className="hidden" onChange={e => setFotoArchivo(e.target.files[0])} />
                    {fotoArchivo || form.foto_url ? (
                      <div className="relative w-full h-full">
                        <img src={fotoArchivo ? URL.createObjectURL(fotoArchivo) : buildAssetUrl(form.foto_url)} className="w-full h-full object-cover" alt="Vista previa del perfil" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                          <Camera className="text-white" size={24} />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-slate-400">
                        <Camera size={24} strokeWidth={1.5} />
                        <span className="text-[10px] font-bold uppercase tracking-tighter">Subir Fotografía</span>
                      </div>
                    )}
                  </label>
                </div>

                <div className="pt-4 space-y-3">
                  <button className="w-full bg-slate-900 text-white py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-slate-200">
                    {editando ? 'Actualizar Miembro' : 'Dar de Alta'}
                  </button>
                  {editando && (
                    <button type="button" onClick={cancelarEdicion} className="w-full text-[10px] font-bold text-slate-400 uppercase tracking-widest hover:text-red-500 transition-colors">
                      Cancelar Edición
                    </button>
                  )}
                </div>
              </form>
            </div>
          </aside>

          {/* LISTADO DE USUARIOS */}
          <main className="lg:col-span-8">
            {loading ? (
              <div className="grid md:grid-cols-2 gap-6 animate-pulse">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-40 bg-slate-200 rounded-[2.5rem]" />)}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {usuarios.map(u => (
                  <div key={u.id} className="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
                    <div className="flex justify-between items-start relative z-10">
                      <div className="flex gap-4">
                        <div className="relative">
                          <img src={u.foto_url ? buildAssetUrl(u.foto_url) : `https://ui-avatars.com/api/?name=${u.nombre}&background=f1f5f9&color=64748b`} className="w-16 h-16 rounded-full object-cover ring-4 ring-slate-50" alt={u.nombre ? `Foto de ${u.nombre}` : 'Foto de usuario'} />
                          {u.rol === 'superadmin' && <div className="absolute -bottom-1 -right-1 bg-slate-900 text-white p-1 rounded-full border-2 border-white"><ShieldCheck size={12}/></div>}
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900 text-lg leading-tight mb-1">{u.nombre}</h3>
                          <p className="text-xs text-slate-400 flex items-center gap-1.5 font-medium">
                            <Mail size={12} className="text-slate-300"/> {u.correo}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => iniciarEdicion(u)} className="p-2 bg-slate-50 text-slate-400 hover:text-blue-600 rounded-xl transition-colors"><Edit3 size={16}/></button>
                        <button onClick={() => eliminarUsuario(u.id)} className="p-2 bg-slate-50 text-slate-400 hover:text-red-600 rounded-xl transition-colors"><Trash2 size={16}/></button>
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-50 flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${getRolBadge(u.rol)}`}>
                        {u.rol.replace('_', ' ')}
                      </span>
                      <div className="text-slate-200">
                        {u.rol.includes('admin') ? <Shield size={16}/> : <Briefcase size={16}/>}
                      </div>
                    </div>
                    {/* Elemento decorativo */}
                    <div className="absolute top-0 right-0 w-24 h-24 bg-slate-50 rounded-full -mr-12 -mt-12 group-hover:bg-blue-50 transition-colors" />
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      <style jsx="true">{`
        .user-input {
          width: 100%;
          padding: 14px 18px;
          border-radius: 16px;
          border: 1px solid #f1f5f9;
          background: #f8fafc;
          font-size: 14px;
          transition: all 0.2s;
          color: #1e293b;
        }
        .user-input:focus {
          outline: none;
          background: #fff;
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59,130,246,0.1);
        }
      `}</style>
    </div>
  );
}
